'use client';

import { RefObject, useCallback, useEffect, useRef } from 'react';

/**
 * Lets a transform-based carousel (react-multi-carousel) be driven by the
 * sideways scroll gestures a native scroller would already answer: a trackpad
 * two-finger swipe, a tilt wheel, and Shift + wheel.
 *
 * A plain vertical wheel is deliberately left alone. These carousels are
 * full-width bands, and swallowing `deltaY` over one would trap the page
 * scroll every time the cursor crossed it — so vertical stays the page's.
 */

/** Accumulated sideways travel that advances one card. */
const STEP_PX = 60;
/** `deltaMode: DOM_DELTA_LINE` — mice that report lines rather than pixels. */
const LINE_PX = 16;
/** A gap this long means a new gesture, so the leftover travel is dropped. */
const IDLE_RESET_MS = 180;
/**
 * Held off for the length of the slide transition. Trackpad momentum keeps
 * firing events long after the fingers lift, and without this a single flick
 * would run the carousel to its end.
 */
const STEP_COOLDOWN_MS = 280;

interface HorizontalWheelNavOptions {
    onPrev: () => void;
    onNext: () => void;
    /** Off while there is nothing to scroll to — a short, unlooped row. */
    enabled?: boolean;
    /**
     * RTL rows run the other way: the next card sits to the *left*, so the
     * gesture that reaches it is a negative `deltaX`.
     */
    invert?: boolean;
}

export const useHorizontalWheelNav = <T extends HTMLElement>({
    onPrev,
    onNext,
    enabled = true,
    invert = false
}: HorizontalWheelNavOptions): RefObject<T> => {
    const ref = useRef<T>(null);

    // Through refs so a caller can pass inline arrows without re-binding the
    // listener (and losing the gesture in progress) on every render.
    const onPrevRef = useRef(onPrev);
    const onNextRef = useRef(onNext);
    const enabledRef = useRef(enabled);
    const invertRef = useRef(invert);
    onPrevRef.current = onPrev;
    onNextRef.current = onNext;
    enabledRef.current = enabled;
    invertRef.current = invert;

    // Horizontal intent only. Shift + wheel is the mouse-only way in, and
    // browsers disagree about which axis they report it on, so take either.
    const horizontalDelta = useCallback((e: WheelEvent, el: HTMLElement) => {
        const raw = e.shiftKey ? e.deltaX || e.deltaY : e.deltaX;
        if (!raw) return 0;
        if (e.deltaMode === 1) return raw * LINE_PX;
        if (e.deltaMode === 2) return raw * el.clientWidth;
        return raw;
    }, []);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        let travel = 0;
        let lastAt = 0;
        let readyAt = 0;

        const onWheel = (e: WheelEvent) => {
            if (!enabledRef.current) return;
            const delta = horizontalDelta(e, el);
            // No sideways component: hands the event straight back, so the
            // page scrolls exactly as it does anywhere else.
            if (!delta) return;

            // Ours now — this also stops the two-finger swipe from being read
            // as a browser back/forward navigation.
            e.preventDefault();

            const now = e.timeStamp || performance.now();
            if (now - lastAt > IDLE_RESET_MS || delta * travel < 0) travel = 0;
            lastAt = now;
            // Still mid-slide: the gesture keeps the page from scrolling but
            // stops piling up, so a flick is one card and not a sprint.
            if (now < readyAt) return;

            travel += delta;
            if (Math.abs(travel) < STEP_PX) return;

            const forward = invertRef.current ? travel < 0 : travel > 0;
            travel = 0;
            readyAt = now + STEP_COOLDOWN_MS;
            (forward ? onNextRef : onPrevRef).current();
        };

        // Not passive: the whole point is to take the gesture off the page.
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [horizontalDelta]);

    return ref;
};

export default useHorizontalWheelNav;
